import React, { useEffect, useState } from "react";
import { Button, Card, Input, Modal, Select, Space, Spin, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
    clearMsEnseignesGroupsCache,
    MsEnseignesGroup,
    MsEnseignesGroupsData,
} from "./msEnseignesGroups-service";

export default function MsGroupsButtonPlusModal() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [enseignes, setEnseignes] = useState<string[]>([]);
    const [groups, setGroups] = useState<MsEnseignesGroup[]>([]);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        setLoading(true);

        axios
            .get<MsEnseignesGroupsData>("/ms/enseignes/groups")
            .then((response) => {
                if (cancelled) {
                    return;
                }
                setEnseignes(response.data.enseignes ?? []);
                setGroups(
                    (response.data.groups ?? []).map((group) => ({
                        id: group.id,
                        name: group.name,
                        enseignes: group.enseignes ?? [],
                    }))
                );
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                console.error("Erreur lors du chargement des groupes MS", error);
                message.error("Erreur lors du chargement des groupes MS");
                setEnseignes([]);
                setGroups([]);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open]);

    function updateGroup(id: string, patch: Partial<MsEnseignesGroup>) {
        setGroups((current) => current.map((group) => (group.id === id ? { ...group, ...patch } : group)));
    }

    function addGroup() {
        setGroups((current) => [
            ...current,
            {
                id: uuidv4(),
                name: `Groupe ${current.length + 1}`,
                enseignes: [],
            },
        ]);
    }

    function removeGroup(id: string) {
        setGroups((current) => current.filter((group) => group.id !== id));
    }

    async function saveGroups() {
        if (groups.some((group) => !group.name.trim())) {
            message.error("Le nom d'un groupe ne peut pas être vide");
            return;
        }

        setSaving(true);
        try {
            await axios.post("/ms/enseignes/groups", {
                groups: groups.map((group) => ({
                    id: group.id,
                    name: group.name.trim(),
                    enseignes: group.enseignes,
                })),
            });
            clearMsEnseignesGroupsCache();
            message.success("Groupes enregistrés");
            setOpen(false);
        } catch (error) {
            console.error("Erreur lors de l'enregistrement des groupes MS", error);
            message.error("Erreur lors de l'enregistrement des groupes MS");
        } finally {
            setSaving(false);
        }
    }

    const enseigneOptions = enseignes.map((enseigne) => ({
        label: enseigne,
        value: enseigne,
    }));

    return (
        <>
            <Button onClick={() => setOpen(true)}>Groupes MS</Button>
            <Modal
                title="Groupes MS"
                open={open}
                onCancel={() => setOpen(false)}
                width={800}
                destroyOnClose
                footer={[
                    <Button key="cancel" onClick={() => setOpen(false)}>
                        Annuler
                    </Button>,
                    <Button key="save" type="primary" loading={saving} disabled={loading} onClick={saveGroups}>
                        Enregistrer
                    </Button>,
                ]}
            >
                {loading ? (
                    <div style={{ textAlign: "center", padding: 40 }}>
                        <Spin size="large" tip="Chargement des enseignes et groupes" />
                    </div>
                ) : (
                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                        <Button type="dashed" icon={<PlusOutlined />} onClick={addGroup} block>
                            Ajouter un groupe
                        </Button>

                        {groups.length === 0 ? (
                            <Typography.Text type="secondary">Aucun groupe pour le moment</Typography.Text>
                        ) : (
                            groups.map((group) => (
                                <Card
                                    styles={{
                                        header: {
                                            borderBottom: "none",
                                        },
                                    }}
                                    key={group.id}
                                    size="small"
                                    title={
                                        <Input style={{ margin: "10px 0 10px 0" }}
                                            value={group.name}
                                            placeholder="Nom du groupe"
                                            onChange={(e) => updateGroup(group.id, { name: e.target.value })}
                                        />
                                    }
                                    extra={
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeGroup(group.id)}
                                        />
                                    }
                                >
                                    <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                                        Enseignes
                                    </Typography.Text>
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        allowClear
                                        style={{ width: "100%" }}
                                        placeholder="Sélectionner des enseignes"
                                        options={enseigneOptions}
                                        value={group.enseignes}
                                        filterOption={(input, option) =>
                                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                        }
                                        onChange={(value: string[]) => updateGroup(group.id, { enseignes: value })}
                                    />
                                </Card>
                            ))
                        )}
                    </Space>
                )}
            </Modal>
        </>
    );
}
